import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Typography,
  CircularProgress,
  Chip,
  Link,
  Tooltip
} from '@mui/material';
import logos from '../../Media/logos';
import { communitySettingsUrl } from '../../serverLinks';

const PAGE_SIZE = 100;

const COLUMNS = [
  { key: 'name', label: 'Name', sortable: true, sticky: true, minWidth: 220 },
  { key: 'client', label: 'Client', sortable: true },
  { key: 'isLive', label: 'Live Status', sortable: true, align: 'center' },
  { key: 'isLiveAt', label: 'Go-Live Date', sortable: true },
  { key: 'totalUnits', label: 'Total Units', sortable: true, align: 'right' },
  {
    key: 'availableUnits',
    label: 'Available Units',
    sortable: true,
    align: 'right'
  },
  { key: 'pmsProvider', label: 'PMS' },
  { key: 'crmProvider', label: 'CRM' },
  { key: 'communityPhone', label: 'Community Phone' },
  { key: 'id', label: 'Community ID' },
  { key: 'callToTextEnabled', label: 'C2T Status', align: 'center' },
  { key: 'address', label: 'Address' },
  { key: 'state', label: 'State' },
  { key: 'timeZone', label: 'Timezone' },
  { key: 'enableBot', label: 'Bot Status', align: 'center' },
  { key: 'isSettingsEnable', label: 'Configured', align: 'center' },
  { key: 'kelseyRestricted', label: 'Kelsey Restricted', align: 'center' }
];

const ProviderChip = ({ value }) => {
  if (!value) return '—';
  const key = String(value).toLowerCase();
  const logo = logos[key];
  if (logo) {
    return (
      <Tooltip title={value} arrow>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 28,
            width: 72,
            px: 1,
            py: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '16px',
            backgroundColor: 'background.paper'
          }}
        >
          <Box
            component="img"
            src={logo}
            alt={value}
            sx={{
              maxHeight: 16,
              maxWidth: '100%',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </Box>
      </Tooltip>
    );
  }
  return (
    <Chip
      size="small"
      label={value}
      variant="outlined"
      color="primary"
      sx={{ textTransform: 'capitalize' }}
    />
  );
};

const formatPhone = phone => {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

const formatDate = dateString => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return '—';
  }
};

const BoolChip = ({ value, trueLabel = 'Yes', falseLabel = 'No' }) => (
  <Chip
    size="small"
    label={value ? trueLabel : falseLabel}
    color={value ? 'success' : 'default'}
    variant={value ? 'filled' : 'outlined'}
  />
);

const CommunitiesTable = ({
  data,
  totalCount,
  page,
  onPageChange,
  sortField,
  sortDirection,
  onSortChange,
  loading
}) => {
  if (loading || data === null) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          minHeight: 0
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          minHeight: 0
        }}
      >
        <Typography color="text.secondary">No communities found</Typography>
      </Box>
    );
  }

  const renderCell = (row, key) => {
    switch (key) {
      case 'name':
        return (
          <Tooltip title={row.name || ''} arrow>
            <Link
              href={communitySettingsUrl(row.clientId, row.id)}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{
                fontWeight: 600,
                display: 'inline-block',
                maxWidth: 220,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                verticalAlign: 'middle'
              }}
            >
              {row.name || '—'}
            </Link>
          </Tooltip>
        );
      case 'isLive':
        return (
          <Chip
            size="small"
            label={row.isLive ? 'Live' : 'Not Live'}
            color={row.isLive ? 'success' : 'default'}
            variant={row.isLive ? 'filled' : 'outlined'}
          />
        );
      case 'isLiveAt':
        return formatDate(row.isLiveAt);
      case 'pmsProvider':
        return <ProviderChip value={row.pmsProvider} />;
      case 'crmProvider':
        return <ProviderChip value={row.crmProvider} />;
      case 'communityPhone':
        return formatPhone(row.communityPhone) || '—';
      case 'id':
        return (
          <Typography
            variant="caption"
            sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
          >
            {row.id}
          </Typography>
        );
      case 'callToTextEnabled':
        return (
          <BoolChip
            value={row.callToTextEnabled}
            trueLabel="Enabled"
            falseLabel="Disabled"
          />
        );
      case 'address':
        return (
          <Tooltip title={row.address || ''} arrow>
            <Typography
              variant="body2"
              sx={{
                maxWidth: 240,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {row.address || '—'}
            </Typography>
          </Tooltip>
        );
      case 'state':
        return row.state || '—';
      case 'timeZone':
        return row.timeZone || '—';
      case 'enableBot':
        return (
          <BoolChip value={row.enableBot} trueLabel="On" falseLabel="Off" />
        );
      case 'isSettingsEnable':
        return <BoolChip value={row.isSettingsEnable} />;
      case 'kelseyRestricted':
        return (
          <Chip
            size="small"
            label={row.kelseyRestricted ? 'Yes' : 'No'}
            color={row.kelseyRestricted ? 'error' : 'success'}
            variant="filled"
          />
        );
      default:
        return row[key] ?? '—';
    }
  };

  return (
    <Paper
      sx={{
        maxWidth: 'calc(100vw - 120px)',
        backgroundColor: 'background.paper',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
        <Table
          stickyHeader
          size="small"
          sx={{ borderCollapse: 'separate', borderSpacing: 0 }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.default' }}>
              {COLUMNS.map(col => (
                <TableCell
                  key={col.key}
                  align={col.align || 'left'}
                  sx={{
                    backgroundColor: 'background.default',
                    color: theme =>
                      theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontSize: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '2px solid',
                    borderBottomColor: 'divider',
                    whiteSpace: 'nowrap',
                    minWidth: col.minWidth,
                    ...(col.sticky && {
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width: '4px',
                        transform: 'translateX(100%)',
                        background: theme =>
                          `linear-gradient(to right, ${
                            theme.palette.mode === 'dark'
                              ? 'rgba(0,0,0,0.45)'
                              : 'rgba(0,0,0,0.08)'
                          }, transparent)`
                      }
                    })
                  }}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={sortField === col.key}
                      direction={sortField === col.key ? sortDirection : 'asc'}
                      onClick={() => onSortChange(col.key)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow
                key={row.id || index}
                hover
                sx={{
                  backgroundColor: 'background.paper',
                  '&:hover': { backgroundColor: 'action.hover' },
                  '& td': {
                    borderBottom: theme =>
                      `1px solid ${theme.palette.divider} !important`
                  }
                }}
              >
                {COLUMNS.map(col => (
                  <TableCell
                    key={col.key}
                    align={col.align || 'left'}
                    sx={{
                      fontSize: '14px',
                      color: 'text.primary',
                      borderBottom: '1px solid',
                      borderBottomColor: 'divider',
                      whiteSpace: 'nowrap',
                      ...(col.sticky && {
                        position: 'sticky',
                        left: 0,
                        backgroundColor: 'background.paper',
                        zIndex: 1,
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          bottom: 0,
                          width: '4px',
                          transform: 'translateX(100%)',
                          background: theme =>
                            `linear-gradient(to right, ${
                              theme.palette.mode === 'dark'
                                ? 'rgba(0,0,0,0.45)'
                                : 'rgba(0,0,0,0.08)'
                            }, transparent)`
                        }
                      })
                    }}
                  >
                    {renderCell(row, col.key)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
        sx={{ borderTop: '1px solid', borderTopColor: 'divider' }}
      />
    </Paper>
  );
};

export { PAGE_SIZE };
export default CommunitiesTable;
